import { useFeedback, useTranslation } from "@opendash/core";
import { Button, Form, Input, Modal, Select, Space } from "antd";
import dayjs, { Dayjs } from "dayjs";
import Parse from "parse";
import React, { useEffect, useState } from "react";
import { useBikesAllowed } from "../hooks/useBikesAllowed";
import { useDirection } from "../hooks/useDirection";
import { useWheelchairAccessible } from "../hooks/useWheelchairAccessible";
import { Calendar, Route, Trip, _User } from "../js/parsegenerated";
import { EndDateSelect } from "./EndDateSelect";
import { StartDateSelect } from "./StartDateSelect";
import Infolabel from "./UI/Infolabel";
import { WeekDaySelect } from "./WeekDaySelect";

export interface TripDataType {
  trip_headsign: string;
  trip_short_name: string;
  direction_id: number;
  wheelchair_accessible: number;
  bikes_allowed: number;
  start_date: Dayjs;
  end_date: Dayjs;
  weekdays: Array<string>;
}

export const TripUpdateModal = (props: {
  open: boolean;
  route_id: string;
  trip_id: string;
  onOk: Function;
  onCancel: Function;
}) => {
  const t = useTranslation();
  const { message } = useFeedback();
  const [loading, setLoading] = useState(false);
  const [startdate, setStartdate] = useState<Dayjs>(dayjs());
  const [directions] = useDirection();
  const [wheelchair_accessible] = useWheelchairAccessible();
  const [bikes_allowed] = useBikesAllowed();
  const [form] = Form.useForm();
  const [trip, setTrip] = useState<Trip>(new Trip());
  const [valuesHaveChanged, setValuesHaveChanged] = useState(false);

  useEffect(() => {}, [props.trip_id]);

  const init = async () => {
    const cTrip: Trip = await (props.trip_id
      ? new Parse.Query(Trip).includeAll().get(props.trip_id)
      : new Promise(() => new Trip()));

    const service: Calendar = cTrip.get("service_id");

    console.log(
      directions,
      cTrip.get("direction_id")?.id,
      directions.find((dir) => dir.id === cTrip.get("direction_id")?.id)?.value
    );

    form.setFieldsValue({
      start_date: service ? dayjs(service.start_date?.getTime()) : dayjs(),
      end_date: service ? dayjs(service.end_date?.getTime()) : dayjs(),
      weekdays: service
        ? [
            service.get("monday") ? "monday" : null,
            service.get("tuesday") ? "tuesday" : null,
            service.get("wednesday") ? "wednesday" : null,
            service.get("thursday") ? "thursday" : null,
            service.get("friday") ? "friday" : null,
            service.get("saturday") ? "saturday" : null,
            service.get("sunday") ? "sunday" : null,
          ].filter((day) => day !== null)
        : [],
      bikes_allowed:
        bikes_allowed.find(
          (bikeAllow) =>
            bikeAllow.id === (cTrip.get("bikes_allowed")?.id || "none")
        )?.value || null,
      direction_id: directions.find(
        (dir) => dir.id === cTrip.get("direction_id")?.id
      )?.value,
      trip_headsign: cTrip.get("trip_headsign"),
      trip_short_name: cTrip.get("trip_short_name"),
      wheelchair_accessible:
        wheelchair_accessible.find(
          (wa) => wa.id === (cTrip.get("wheelchair_accessible")?.id || "none")
        )?.value || null,
    });
    setTrip(cTrip);
  };
  useEffect(() => {
    if (props.open) {
      init();
    }
  }, [props.trip_id, props.route_id, props.open, directions]);

  async function save() {
    form.validateFields().then(async (values: TripDataType) => {
      setLoading(true);

      //   Find the selected route
      const trip = await new Parse.Query(Trip).get(props.trip_id);

      //  Create the calendar entries
      const serivce: Calendar = trip.get("service_id");
      if (values.weekdays && values.weekdays.length > 0) {
        const savedservice = await serivce.save({
          start_date: new Date(values.start_date.toISOString()),
          end_date: new Date(values.end_date.toISOString()),
          monday: values.weekdays?.includes("monday"),
          tuesday: values.weekdays?.includes("tuesday"),
          wednesday: values.weekdays?.includes("wednesday"),
          thursday: values.weekdays?.includes("thursday"),
          friday: values.weekdays?.includes("friday"),
          saturday: values.weekdays?.includes("saturday"),
          sunday: values.weekdays?.includes("sunday"),
          tenant: (Parse.User.current() as _User).get("tenant"),
        });
      }

      const route = await new Parse.Query(Route).get(props.route_id);
      await trip.save({
        bikes_allowed: bikes_allowed.find(
          (status) => status.value === values.bikes_allowed
        ),
        direction_id: directions.find(
          (status) => status.value === values.direction_id
        ),
        trip_headsign: values.trip_headsign,
        trip_short_name: values.trip_short_name,
        wheelchair_accessible: wheelchair_accessible.find(
          (status) => status.value === values.wheelchair_accessible
        ),
        route_id: route,
        service_id: serivce,
        tenant: (Parse.User.current() as _User).get("tenant"),
      });
      message.success(t("gtfs:trip.update.modal.success"));
      props.onOk();
      form.resetFields();

      setLoading(false);
    });
  }

  return (
    <>
      {props.open && (
        <Modal
          title={t("gtfs:trip.update.modal.title")}
          open={props.open}
          onOk={save}
          onCancel={() => {
            props.onCancel();
          }}
          width={"80%"}
          getContainer={false}
          footer={[
            <Button
              key="back"
              onClick={() => {
                props.onCancel();
              }}
            >
              {t("gtfs:global.button.cancel_label")}
            </Button>,
            <Button
              key="submit"
              type="primary"
              loading={loading}
              onClick={save}
            >
              {t("gtfs:global.button.save_label")}
            </Button>,
          ]}
        >
          <Form
            autoComplete="off"
            form={form}
            layout="vertical"
            onValuesChange={() => {
              setValuesHaveChanged(true);
            }}
          >
            <Space direction="vertical" size="large">
              <Form.Item
                name="trip_headsign"
                label={
                  <Infolabel
                    title={t("gtfs:trip.create.modal.form.trip_headsign.label")}
                    info={t("gtfs:trip.create.modal.form.trip_headsign.info")}
                  />
                }
                help={t("gtfs:trip.create.modal.form.trip_headsign.help")}
                initialValue={trip.get("trip_headsign")}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="trip_short_name"
                label={
                  <Infolabel
                    title={t(
                      "gtfs:trip.create.modal.form.trip_short_name.label"
                    )}
                    info={t("gtfs:trip.create.modal.form.trip_short_name.info")}
                  />
                }
                help={t("gtfs:trip.create.modal.form.trip_short_name.help")}
                initialValue={trip.get("trip_short_name")}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="direction_id"
                label={
                  <Infolabel
                    title={t("gtfs:trip.create.modal.form.direction_id.label")}
                    info={t("gtfs:trip.create.modal.form.direction_id.info")}
                  />
                }
                help={t("gtfs:trip.create.modal.form.direction_id.help")}
              >
                <Select
                  options={directions.map((direction) => {
                    return {
                      value: direction.value,
                      label: direction.value + ": " + direction.description,
                    };
                  })}
                />
              </Form.Item>
              <Form.Item
                name="wheelchair_accessible"
                label={t(
                  "gtfs:trip.create.modal.form.wheelchair_accessible.label"
                )}
                help={t(
                  "gtfs:trip.create.modal.form.wheelchair_accessible.help"
                )}
                initialValue={trip.get("wheelchair_accessible")}
              >
                <Select
                  options={wheelchair_accessible.map((wa) => {
                    return {
                      value: wa.value,
                      label: wa.value + ": " + wa.description,
                    };
                  })}
                />
              </Form.Item>
              <Form.Item
                name="bikes_allowed"
                label={t("gtfs:trip.create.modal.form.bikes_allowed.label")}
                help={t("gtfs:trip.create.modal.form.bikes_allowed.help")}
                initialValue={trip.get("bikes_allowed")}
              >
                <Select
                  options={bikes_allowed.map((ba) => {
                    return {
                      value: ba.value,
                      label: ba.value + ": " + ba.description,
                    };
                  })}
                />
              </Form.Item>
              <Form.Item name="weekdays">
                <WeekDaySelect />
              </Form.Item>
              <Form.Item
                label={t(
                  "gtfs:trip.create.modal.form.service.start_date.label"
                )}
                name="start_date"
                help={t("gtfs:trip.create.modal.form.service.start_date.help")}
                rules={[
                  {
                    required: true,
                    message: t(
                      "gtfs:trip.create.modal.form.service.start_date.rules.required"
                    ),
                  },
                ]}
              >
                <StartDateSelect
                  onChange={(newdate: Dayjs) => {
                    setStartdate(newdate);
                  }}
                />
              </Form.Item>

              <Form.Item
                label={t("gtfs:trip.create.modal.form.service.end_date.label")}
                name="end_date"
                help={t("gtfs:trip.create.modal.form.service.end_date.help")}
                rules={[
                  {
                    required: true,
                    message: t(
                      "gtfs:trip.create.modal.form.service.end_date.rules.required"
                    ),
                  },
                ]}
              >
                <EndDateSelect
                  startdate={startdate}
                  onChange={(v) => console.log(v, dayjs())}
                />
              </Form.Item>
            </Space>
          </Form>
        </Modal>
      )}
    </>
  );
};
